import { HTMLAttributes, useEffect, useState } from 'react';
import { ReactComponent as QL } from '../../assets/svg/DoubleQuotesLeft.svg';
import { ReactComponent as QR } from '../../assets/svg/DoubleQuotesRight.svg';

export interface PaginatorPageChangeEvent {
    first: number,
    rows: number,
    page: number,
    pageCount: number,
}

export interface PaginationProps extends HTMLAttributes<HTMLParagraphElement> {
    first: number,
    rows: number,
    totalRecords: number,
    onPageChange: (e: PaginatorPageChangeEvent) => void
    maxPages?: number,
    children?: React.ReactNode,
}

function Pagination({ first, rows, totalRecords, onPageChange, maxPages, ...props }: PaginationProps) {
    const [page, setPage] = useState<number>(0)
    const [pages, setPages] = useState<number[]>([])

    const last = page > ((totalRecords - rows) / rows - 1);
    const pageCount = Math.ceil(totalRecords / rows);

    const pageRangeDisplayed = maxPages || 5;
    let leftSide = Math.floor(pageRangeDisplayed / 2);
    let rightSide = pageRangeDisplayed - leftSide;

    if (page > pageCount - pageRangeDisplayed / 2) {
        rightSide = pageCount - page;
        leftSide = pageRangeDisplayed - rightSide;
    } else if (page < pageRangeDisplayed / 2) {
        leftSide = page;
        rightSide = pageRangeDisplayed - leftSide;
    }

    useEffect(() => {
        setPage(Math.floor(first / rows));
    }, [first]);

    useEffect(() => {
        const from = Math.max(0, page - leftSide);
        const to = Math.min(pageCount, page + rightSide);
        const _pages: number[] = [];
        for (let i = from; i < to; i++) {
            _pages.push(i);
        }
        if (!_pages.length) {
            _pages.push(0);
        }
        setPages(_pages);
    }, [page, totalRecords]);

    return (
        <div className="pagination" {...props}>
            {page === 0 ?
                <span className="pagination__arrow">
                    <QL />
                </span>
                :
                <a href="#" className="pagination__arrow" onClick={(e) => {
                    e.preventDefault();
                    onPageChange({ first: 0, page: 0, pageCount: pageCount, rows: rows });
                }}>
                    <QL />
                </a>
            }
            <div className="pagination__list">
                {pages.map((p) =>
                    p === page ?
                        <strong key={p} className="pagination__item _active">{p + 1}</strong>
                        :
                        <a href="#" key={p} className="pagination__item" onClick={(e) => {
                            e.preventDefault();
                            onPageChange({ first: p * rows, page: p, pageCount: pageCount, rows: rows });
                        }}>{p + 1}</a>
                )}
            </div>
            {last ?
                <span className="pagination__arrow">
                    <QR />
                </span>
                :
                <a href="#" className="pagination__arrow" onClick={(e) => {
                    e.preventDefault();
                    const p = pageCount - 1;
                    onPageChange({ first: p * rows, page: p, pageCount: pageCount, rows: rows });
                }}>
                    <QR />
                </a>
            }
        </div>
    );
}

export default Pagination;
